// Match Day — the match screen: clock bar, On pitch / Bench / Subbed off with live minutes, the
// sub flow (tap off, tap on, Log sub) and the sub log (correct time, delete, undo, copy).
import React, { useMemo, useState } from 'react';
import { Copy, Undo2, Trash2, X, RotateCcw } from 'lucide-react';
import { displayClock, eventMatchSeconds, formatMinSec } from './engine';
import {
  advanceClock, undoClock, nextClockStep, lastClockStep, logSub, correctSub, deleteSub, clearAllSubs, resetMatch,
} from './matchdayStore';
import { Btn, Badge, C, ConfirmBtn, fmtTime } from './ui';

const STEP_LABEL = { h1Start: 'Kick-off', h1End: 'Half-time', h2Start: '2nd half start', h2End: 'Full time' };
const PHASE_LABEL = { pre: 'Before kick-off', h1: '1st half', ht: 'Half-time', h2: '2nd half', ft: 'Full time' };

export function playerLabel(p) {
  if (!p) return '—';
  return `${p.squadNumber != null ? `${p.squadNumber} ` : ''}${p.name}`;
}

// "21:34:45" → an ISO instant on the same local date as `baseIso`; null if it doesn't parse.
function parseWallTime(text, baseIso) {
  const m = /^\s*(\d{1,2})[:.](\d{2})(?:[:.](\d{2}))?\s*$/.exec(text || '');
  if (!m) return null;
  const [h, mi, s] = [Number(m[1]), Number(m[2]), Number(m[3] || 0)];
  if (h > 23 || mi > 59 || s > 59) return null;
  const d = new Date(baseIso || Date.now());
  d.setHours(h, mi, s, 0);
  return d.toISOString();
}

export function buildLogText(state, playersById) {
  const f = state.fixture || {};
  const c = state.clock || {};
  const lines = [`Heracles vs ${f.opponent || '?'} (${f.homeAway || '?'}) ${f.matchDate || ''}`];
  lines.push(`Kick-off ${fmtTime(c.h1Start)} · Half-time ${fmtTime(c.h1End)} · 2nd half ${fmtTime(c.h2Start)} · Full time ${fmtTime(c.h2End)}`);
  state.events.filter(e => !e.deletedAt)
    .slice().sort((a, b) => a.occurredAt.localeCompare(b.occurredAt))
    .forEach(e => {
      lines.push(`${fmtTime(e.occurredAt)} (${formatMinSec(eventMatchSeconds(e))}) OFF ${e.playerOff ? playerLabel(playersById.get(e.playerOff)) : '—'} · ON ${e.playerOn ? playerLabel(playersById.get(e.playerOn)) : '—'}`);
    });
  return lines.join('\n');
}

function ClockBar({ state, commit, now, hasSubs }) {
  const clock = displayClock(state.clock, now);
  const next = nextClockStep(state.clock);
  const last = lastClockStep(state.clock);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 16, padding: 16, borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--border-strong)' }}>
      <div style={{ minWidth: 200 }}>
        <div style={{ fontSize: 64, fontWeight: 800, lineHeight: 1, fontVariantNumeric: 'tabular-nums' }}>
          {clock.phase === 'ht' ? 'HT' : formatMinSec(clock.seconds)}
        </div>
        <div style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 6 }}>
          {PHASE_LABEL[clock.phase]} · {new Date(now).toLocaleTimeString('nl-NL', { hour12: false })}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginLeft: 'auto' }}>
        {(last || hasSubs) && (
          <ConfirmBtn confirmLabel="Tap again: clear clock + all subs" onConfirm={() => commit(s => resetMatch(s))}>
            <RotateCcw size={18} /> Reset match
          </ConfirmBtn>
        )}
        {last && (
          <ConfirmBtn confirmLabel={`Tap again: undo ${STEP_LABEL[last].toLowerCase()} (${fmtTime(state.clock[last])})`} onConfirm={() => commit(s => undoClock(s))}>
            <Undo2 size={18} /> Undo {STEP_LABEL[last].toLowerCase()}
          </ConfirmBtn>
        )}
        <Btn big tone={next ? 'primary' : 'default'} disabled={!next} onClick={() => commit(s => advanceClock(s))}>
          {next ? STEP_LABEL[next] : 'Full time ✓'}
        </Btn>
      </div>
    </div>
  );
}

// The exact moment a player went off / came on: wall clock + match clock, e.g. "off 21:34:55 · 55:00".
function SubMoment({ moment }) {
  if (!moment) return null;
  const color = moment.kind === 'off' ? C.red : C.green;
  return (
    <span style={{ display: 'block', fontSize: 13, fontWeight: 700, color, fontVariantNumeric: 'tabular-nums' }}>
      {moment.kind} {fmtTime(moment.event.occurredAt)} · {formatMinSec(eventMatchSeconds(moment.event))}
    </span>
  );
}

function PlayerTile({ p, row, selected, selColor, onTap, moment }) {
  return (
    <button type="button" onClick={onTap}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 56, padding: '8px 12px',
        borderRadius: 12, textAlign: 'left', cursor: 'pointer', touchAction: 'manipulation',
        border: `2px solid ${selected ? selColor : 'var(--border)'}`,
        background: selected ? `${selColor}22` : 'var(--bg-app)', color: 'var(--text-primary)',
      }}>
      <span style={{ width: 30, fontWeight: 800, fontSize: 18, color: 'var(--text-muted)' }}>{p?.squadNumber ?? ''}</span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: 16, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {p?.name || `#${row.playerId}`} {p?.isGoalkeeper && <Badge style={{ marginLeft: 4 }}>GK</Badge>}
        </span>
        <SubMoment moment={moment} />
      </span>
      <span style={{ fontSize: 18, fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{formatMinSec(row.seconds)}</span>
    </button>
  );
}

function List({ title, rows, playersById, color, selectedId, onTap, moments }) {
  return (
    <div style={{ flex: '1 1 260px', minWidth: 0 }}>
      <div style={{ fontSize: 13, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.5, color: 'var(--text-muted)', margin: '0 0 8px' }}>
        {title} ({rows.length})
      </div>
      <div style={{ display: 'grid', gap: 8 }}>
        {rows.map(r => (
          <PlayerTile key={r.playerId} p={playersById.get(r.playerId)} row={r} selColor={color}
            selected={selectedId === r.playerId} onTap={() => onTap(r.playerId)} moment={moments.get(r.playerId)} />
        ))}
        {!rows.length && <div style={{ fontSize: 14, color: 'var(--text-dimmed)', padding: 8 }}>—</div>}
      </div>
    </div>
  );
}

function LogEntry({ e, playersById, commit, open, onToggle }) {
  const [text, setText] = useState(fmtTime(e.occurredAt));
  const [error, setError] = useState(null);
  const save = () => {
    const iso = parseWallTime(text, e.occurredAt);
    if (!iso) { setError('Use HH:MM:SS, e.g. 21:34:45'); return; }
    setError(null);
    commit(s => correctSub(s, e.id, iso));
    onToggle();
  };
  const remove = () => { commit(s => deleteSub(s, e.id)); onToggle(); };
  return (
    <div style={{ borderBottom: '1px solid var(--border)' }}>
      <button type="button" onClick={onToggle}
        style={{ display: 'flex', gap: 12, alignItems: 'baseline', width: '100%', minHeight: 48, padding: '8px 4px', background: 'transparent', border: 'none', color: 'var(--text-primary)', textAlign: 'left', cursor: 'pointer', fontSize: 15 }}>
        <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(e.occurredAt)}</span>
        <span style={{ color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' }}>({formatMinSec(eventMatchSeconds(e))})</span>
        <span><b style={{ color: C.red }}>OFF</b> {e.playerOff ? playerLabel(playersById.get(e.playerOff)) : '—'}</span>
        <span><b style={{ color: C.green }}>ON</b> {e.playerOn ? playerLabel(playersById.get(e.playerOn)) : '—'}</span>
      </button>
      {open && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', padding: '4px 4px 12px' }}>
          <span style={{ fontSize: 14, color: 'var(--text-muted)' }}>Correct time</span>
          <input type="text" inputMode="numeric" value={text} onChange={ev => setText(ev.target.value)}
            onKeyDown={ev => { if (ev.key === 'Enter') save(); }}
            style={{ width: 120, minHeight: 44, padding: '0 10px', borderRadius: 10, fontSize: 18, fontWeight: 600, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums' }} />
          <Btn tone="primary" onClick={save}>Save</Btn>
          <ConfirmBtn tone="red" confirmLabel="Tap again: delete" onConfirm={remove}><Trash2 size={16} /> Delete</ConfirmBtn>
          <Btn tone="ghost" onClick={onToggle}><X size={16} /> Cancel</Btn>
          {error && <span style={{ color: C.red, fontWeight: 700, fontSize: 14 }}>{error}</span>}
        </div>
      )}
    </div>
  );
}

export default function MatchTab({ state, commit, minutes, playersById, now }) {
  const [offId, setOffId] = useState(null);
  const [onId, setOnId] = useState(null);
  const [openLog, setOpenLog] = useState(null);
  const [copyText, setCopyText] = useState(null);
  const [copied, setCopied] = useState(false);

  const lists = useMemo(() => {
    const num = id => playersById.get(id)?.squadNumber ?? 999;
    const rows = minutes.players.slice().sort((a, b) => num(a.playerId) - num(b.playerId));
    return { on: rows.filter(r => r.status === 'on'), bench: rows.filter(r => r.status === 'bench'), off: rows.filter(r => r.status === 'off') };
  }, [minutes, playersById]);

  const log = useMemo(
    () => state.events.filter(e => !e.deletedAt).slice().sort((a, b) => b.occurredAt.localeCompare(a.occurredAt)),
    [state.events]);
  // Each player's latest sub moment, for his tile: 'off' if he went off last, 'on' if he came on last.
  const moments = useMemo(() => {
    const m = new Map();
    log.slice().reverse().forEach(e => {
      if (e.playerOff != null) m.set(e.playerOff, { kind: 'off', event: e });
      if (e.playerOn != null) m.set(e.playerOn, { kind: 'on', event: e });
    });
    return m;
  }, [log]);
  const doLog = () => {
    commit(s => logSub(s, { playerOff: offId, playerOn: onId }));
    setOffId(null); setOnId(null);
  };
  const undoLast = () => { const last = log[0]; if (last) commit(s => deleteSub(s, last.id)); };
  const copyLog = async () => {
    const text = buildLogText(state, playersById);
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch { setCopyText(text); }
  };

  return (
    <div style={{ display: 'grid', gap: 16, paddingBottom: 96 }}>
      <ClockBar state={state} commit={commit} now={now} hasSubs={log.length > 0} />
      {minutes.phase === 'pre' && !state.squad.some(s => !s.deletedAt && s.role === 'starter') && (
        <div style={{ color: C.amber, fontWeight: 700 }}>No starters picked yet — set them on the Squad tab.</div>
      )}

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
        <List title="On pitch" rows={lists.on} playersById={playersById} color={C.red} selectedId={offId} moments={moments}
          onTap={id => setOffId(offId === id ? null : id)} />
        <List title="Bench" rows={lists.bench} playersById={playersById} color={C.green} selectedId={onId} moments={moments}
          onTap={id => setOnId(onId === id ? null : id)} />
        <List title="Subbed off" rows={lists.off} playersById={playersById} color={C.green} selectedId={onId} moments={moments}
          onTap={id => setOnId(onId === id ? null : id)} />
      </div>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-strong)', borderRadius: 16, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 800, marginRight: 'auto' }}>Sub log ({log.length})</div>
          <ConfirmBtn disabled={!log.length} confirmLabel={log[0] ? `Tap again: undo ${fmtTime(log[0].occurredAt)}` : ''} onConfirm={undoLast}>
            <Undo2 size={16} /> Undo last sub
          </ConfirmBtn>
          <ConfirmBtn disabled={!log.length} confirmLabel={`Tap again: clear all ${log.length} subs`} onConfirm={() => commit(s => clearAllSubs(s))}>
            <Trash2 size={16} /> Clear all subs
          </ConfirmBtn>
          <Btn tone="ghost" onClick={copyLog}><Copy size={16} /> {copied ? 'Copied ✓' : 'Copy log'}</Btn>
        </div>
        {log.map(e => (
          <LogEntry key={`${e.id}:${e.clientUpdatedAt}`} e={e} playersById={playersById} commit={commit}
            open={openLog === e.id} onToggle={() => setOpenLog(openLog === e.id ? null : e.id)} />
        ))}
        {!log.length && <div style={{ fontSize: 14, color: 'var(--text-dimmed)' }}>No subs yet.</div>}
        {copyText && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 4 }}>Copy didn't work here — select the text below:</div>
            <textarea readOnly value={copyText} rows={Math.min(14, copyText.split('\n').length + 1)} onFocus={e => e.target.select()}
              style={{ width: '100%', fontSize: 14, padding: 8, borderRadius: 8, background: 'var(--bg-app)', color: 'var(--text-primary)', border: '1px solid var(--border-strong)' }} />
            <Btn tone="ghost" onClick={() => setCopyText(null)}>Close</Btn>
          </div>
        )}
      </div>

      {/* Sub bar — fixed at the bottom so it's always in thumb reach. */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 20, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 16px calc(12px + env(safe-area-inset-bottom))', background: 'var(--bg-card)', borderTop: '1px solid var(--border-strong)',
      }}>
        <span style={{ fontSize: 16 }}><b style={{ color: C.red }}>OFF</b> {offId ? playerLabel(playersById.get(offId)) : '—'}</span>
        <span style={{ fontSize: 16 }}><b style={{ color: C.green }}>ON</b> {onId ? playerLabel(playersById.get(onId)) : '—'}</span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {(offId || onId) && <Btn tone="ghost" onClick={() => { setOffId(null); setOnId(null); }}>Clear</Btn>}
          <Btn big tone="green" disabled={!offId && !onId} onClick={doLog}>Log sub</Btn>
        </div>
      </div>
    </div>
  );
}
